import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Sparkles, RefreshCw, Copy, Check } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const THEME_STYLE = {
  action: { from: "#0B1F3A", to: "#1E3A5F", accent: "#D4AF37", glow: "rgba(212,175,55,.45)" },
  sales: { from: "#0F3D3E", to: "#146C6B", accent: "#F5D06F", glow: "rgba(20,108,107,.45)" },
  teamwork: { from: "#2B1B4F", to: "#4A2E8A", accent: "#FFC857", glow: "rgba(74,46,138,.45)" },
  resilience: { from: "#5A1E1E", to: "#9B2C2C", accent: "#FFD166", glow: "rgba(155,44,44,.45)" },
  wealth: { from: "#1C3B22", to: "#2F6B3A", accent: "#E9C46A", glow: "rgba(47,107,58,.45)" },
  growth: { from: "#3A2A0B", to: "#8A5A0B", accent: "#FFE08A", glow: "rgba(138,90,11,.45)" },
};

export default function QuoteOfTheDay() {
  const [q, setQ] = useState(null);
  const [offset, setOffset] = useState(0);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async (o) => {
    const { data } = await api.get(`/quotes/today?offset=${o}&tz_offset=${new Date().getTimezoneOffset()}`);
    setQ(data);
  }, []);
  useEffect(() => { load(offset); }, [offset, load]);

  if (!q) return null;
  const s = THEME_STYLE[q.theme] || THEME_STYLE.action;
  const copy = async () => {
    try { await navigator.clipboard.writeText(`"${q.quote}" — ${q.author}`); setCopied(true); toast.success("Quote copied"); setTimeout(() => setCopied(false), 1500); } catch { toast.error("Copy not available"); }
  };

  return (
    <div className="relative w-full sm:w-[420px] lg:w-[520px]" data-testid="quote-of-the-day">
      <AnimatePresence mode="wait">
        <motion.div key={q.id} initial={{ opacity: 0, y: 14, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10, scale: 0.98 }} transition={{ duration: 0.35 }}
          className="relative rounded-2xl p-5 pb-4 text-white shadow-xl overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${s.from}, ${s.to})`, boxShadow: `0 18px 40px -18px ${s.glow}, 0 0 0 1px rgba(255,255,255,.06)` }}>
          <div className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-25" style={{ background: `radial-gradient(circle, ${s.accent}, transparent 70%)` }} />
          <Quote className="pointer-events-none absolute -bottom-3 -left-2 w-28 h-28 opacity-[.08]" style={{ color: s.accent }} />
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[.18em] px-2.5 py-1 rounded-full" style={{ background: "rgba(255,255,255,.12)", color: s.accent }} data-testid="quote-theme">
              <Sparkles className="w-3 h-3" />{q.weekday} · {q.theme_label}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={copy} className="p-1.5 rounded-full hover:bg-white/15 transition-[background-color]" title="Copy quote" data-testid="quote-copy">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}</button>
              <button onClick={() => setOffset((o) => o + 1)} className="p-1.5 rounded-full hover:bg-white/15 transition-[background-color]" title="Another quote from today's theme" data-testid="quote-next"><RefreshCw className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <p className="font-display text-lg lg:text-xl font-bold leading-snug tracking-tight" data-testid="quote-text">“{q.quote}”</p>
          <div className="mt-3 flex items-end justify-between gap-3">
            <p className="text-sm font-semibold" style={{ color: s.accent }} data-testid="quote-author">— {q.author}</p>
            <p className="text-[10px] text-white/60 text-right leading-tight max-w-[200px]">{q.best_use}</p>
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="absolute -bottom-2 left-10 w-5 h-5 rotate-45 rounded-sm" style={{ background: s.to }} />
    </div>
  );
}
